import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FIELD_EMAIL, FIELD_FIRST_NAME, FIELD_LAST_NAME } from '@components/dynamic-input-fields/src/utils';
import { createFieldsFromFormFields } from '@components/forms/utils/create-fields-from-form-fields';
import { FormField, FormValue } from '@components/modals/add-holder-modal/src/add-holder-modal.type';
import { DATE_FORMAT_SHORT_DATE } from '@constants/datetime';
import { BaseProcessStep, STEP_SELECT_TYPE, STEP_SUCCESS } from '@constants/process-steps';
import { XS_VIEWPORT } from '@constants/viewport';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { Admission, BOOKING_FORM_FIELDS } from '@domain/admission';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useCreateBoxOffice } from '@hooks/box-office/use-create-box-office';
import { useProcessForm } from '@hooks/use-process-form/use-process-form';
import { UseProcessFormReturn } from '@hooks/use-process-form/use-process-form.types';
import { useWindowSize } from '@hooks/use-window-size';
import { downloadFile } from '@pages/events/[companyId]/[clubSlug]/customers/utils';
import { handlePdf } from '@utils/download-file';
import moment from 'moment';

import { BoxOfficeSteps } from './box-office-steps';
import { BoxOfficeForm, ProcessBoxOfficeStep } from './process-box-office.type';

// Custom FormValue interface for box office that supports addOns with quantity
interface BoxOfficeFormValue extends Omit<FormValue, 'addOns'> {
  addOns?: { id: string; quantity: number }[];
}

/*
 * ProcessBoxOffice Component
 *
 * This component handles the Box Office process for creating bookings (tickets) at the event box office.
 * Uses the shared process form hook for common functionality and passes data to the steps component.
 */

export const ProcessBoxOffice = () => {
  const { t } = useTranslation();
  const mobileViewport = useWindowSize(XS_VIEWPORT);
  const formRef = useRef<HTMLFormElement>(null);
  const { isWebView } = useContext(FeatureFlagContext);

  // Use shared process form hook with proper typing - include addons support
  const processForm: UseProcessFormReturn = useProcessForm({
    includeAddOns: true,
    additionalDefaults: {
      bookingFormFields: {},
    },
  });
  // Use step from processForm instead of local state
  const { step: processStep, setStep: setProcessStep, addOns } = processForm;

  // Type-safe wrappers to match box office step types
  const step = processStep as ProcessBoxOfficeStep;
  const setStep = (newStep: ProcessBoxOfficeStep) => {
    setProcessStep(newStep as BaseProcessStep);
  };
  const [pdfFile, setPdfFile] = useState<string | Blob>('');
  const [bookingId, setBookingId] = useState<string>('');
  const [isOpenPdfFileModal, setIsOpenPdfFileModal] = useState<boolean>(false);

  // Fetch addons for the selected offer
  const { data: addons } = useGetOfferAddon({
    offerId: processForm.offer?.id,
    enabled: processForm.offer && processForm.offer?.addOns?.length > 0,
  });
  const availableAddons = addons?.filter((addon) => {
    if (!addon.quantity) return true;

    return addon.soldItems < addon.quantity;
  });

  // Box office specific mutation
  const {
    mutate: boxOfficeMutation,
    isSuccess: isBoxOfficeSuccess,
    data: boxOfficeData,
    reset: resetBoxOfficeMutation,
  } = useCreateBoxOffice();

  const createBoxOffice = (formValue: FormValue) => boxOfficeMutation({ formValue, isWebView });

  const onSubmit = (_data: unknown) => {
    const formData = processForm.methods.getValues() as BoxOfficeForm;
    const calculateAddons = formData.addOns
      ? typeof formData.addOns === 'string'
        ? [{ id: formData.addOns, quantity: formData.quantity }]
        : formData.addOns.map((addon) => ({ id: addon, quantity: formData.quantity }))
      : [];
    // Create box office form value with properly formatted addOns
    const boxOfficeFormValue: BoxOfficeFormValue = {
      offerId: formData.offerId,
      bookingFormFields: createFieldsFromFormFields<BoxOfficeForm>(formData, BOOKING_FORM_FIELDS) as FormField[],
      numTickets: formData.quantity,
      channelId: formData.channelId,
      addOns: calculateAddons,
    };
    // Type assertion since the API expects this format despite the interface
    createBoxOffice(boxOfficeFormValue as FormValue);
  };

  const newBoxOfficeProcess = () => {
    resetBoxOfficeMutation();
    setStep(STEP_SELECT_TYPE);
  };

  // Box office specific field filtering
  const boxOfficeFields = [FIELD_FIRST_NAME, FIELD_LAST_NAME, FIELD_EMAIL];
  const filteredBookingFormFields = (admission: Admission) =>
    admission.bookingFormFields.filter((field) => boxOfficeFields.includes(field.slug));
  const filteredOffer = (admission: Admission) => ({
    ...admission,
    bookingFormFields: filteredBookingFormFields(admission),
  });

  // File handling logic
  const handleFile = useCallback(
    (file: string | Blob, filename?: string) => {
      if (isWebView) {
        const time = moment().format(DATE_FORMAT_SHORT_DATE);
        return downloadFile(file as Blob, 'pdf', filename ?? `box-office-${processForm.offerId}-${time}`);
      }
      if (mobileViewport) return handlePdf({ file: file as string, name: 'box-office', id: bookingId });
      return setIsOpenPdfFileModal(true);
    },
    [mobileViewport, bookingId, isWebView, processForm.offerId]
  );

  const handleSecondarySuccessAction = () => {
    handleFile(pdfFile, boxOfficeData?.filename);
  };

  // Effects for box office specific logic
  useEffect(() => {
    if (isBoxOfficeSuccess && boxOfficeData) {
      const file = isWebView ? boxOfficeData.blob : boxOfficeData.file;
      const filename = boxOfficeData.filename;
      setPdfFile(file);
      setBookingId(isWebView ? '' : boxOfficeData.id);
      setStep(STEP_SUCCESS);
      handleFile(file, filename);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWebView, isBoxOfficeSuccess, boxOfficeData, handleFile]);

  // Get form values
  const formData = processForm.methods.getValues() as BoxOfficeForm;
  const email = formData.bookingFormFields?.email;

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      <FormProvider {...processForm.methods}>
        <BoxOfficeSteps
          step={step}
          setStep={setStep}
          offer={processForm.offer}
          event={processForm.event}
          quantity={processForm.quantity}
          availableAddons={availableAddons ?? []}
          selectedAddonIds={addOns}
          onSubmit={processForm.methods.handleSubmit(onSubmit)}
          onClose={processForm.closeModal}
          filteredOffer={filteredOffer}
          newBoxOfficeProcess={newBoxOfficeProcess}
          handleSecondarySuccessAction={handleSecondarySuccessAction}
          boxOfficeData={boxOfficeData}
          email={email}
          pdfFile={pdfFile}
          isOpenPdfFileModal={isOpenPdfFileModal}
          setIsOpenPdfFileModal={setIsOpenPdfFileModal}
          mobileViewport={mobileViewport}
          isWebView={isWebView}
          t={t}
        />
      </FormProvider>
    </form>
  );
};
